// Blog / Knowledge Centre data.
//
// 15 entries: 6 published (with `body`), 9 stubs (`status: "stub"`, `body: null`).
// Positioning rules enforced across all copy:
//   - No regulatory claims (FDA / GMP / ISO / CE).
//   - No comparative or superlative claims ("better than", "superior to").
//   - No fabricated customer names or invented specifications.
//   - Company is Dubai-based supplier via a specialist supply network.

import { siteConfig } from "@/config/siteConfig";

const CO = siteConfig.COMPANY_NAME;

// ---------- Image key → src map (reused Phase 1 imagery) ----------
export const HERO_IMAGE_MAP = {
  blister: "/images/home/hero-blister-line.jpg",
  coding: "/images/home/problem-tij-coding.jpg",
  sample: "/images/home/sample-flatlay.jpg",
  facility: "/images/home/industry-pharma-facility.jpg",
};

// ---------- Categories ----------
export const CATEGORIES = [
  "Blister Printing",
  "Coding & Marking",
  "Application Guidance",
  "Substrates",
  "Supply Chain",
  "Distribution",
];

// URL-safe slug ← category label
export const categoryToSlug = (label) => label.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\s+/g, "-");
export const slugToCategory = (slug) => CATEGORIES.find((c) => categoryToSlug(c) === slug) || null;

// Estimate reading time from a markdown string (~220 wpm)
export const readingTimeFor = (markdown) => {
  if (!markdown) return 1;
  const words = markdown.replace(/[`#*_>[\]()]/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

// ---------- Article bodies ----------

const bodyBlisterInkSelection = `
Selecting the right ink for pharmaceutical blister printing is rarely a one-variable decision. The foil substrate, the print process, the intended message legibility, the drying conditions on the line, and the compatibility with any coating or lacquer already applied all contribute to whether a chosen ink will perform reliably in regular production.

At ${CO} we work with pharmaceutical manufacturers to supply specialist inks intended for blister-pack printing. Every recommendation is preceded by a review of the customer's actual application — because a product that prints cleanly on one aluminium foil grade may behave differently on another.

## Substrate compatibility

Aluminium blister foils are supplied by numerous mills with different surface treatments, lacquers, and roughness profiles. An ink formulated for a hard-lacquered foil may exhibit poor adhesion on a softer surface, and vice versa. Before recommending an ink we typically ask for:

- Foil supplier and grade reference
- Existing lacquer / heat-seal coating on the print side
- Print orientation (print-then-form or form-then-print)
- Any secondary process the printed foil will pass through (deep-draw forming, sealing, autoclaving)

If the foil grade changes on a repeat order, the appropriate response is a small-scale trial rather than an assumption that the previous ink will continue to perform.

## Print process and equipment

Rotogravure, flexographic, and pad-printing processes place different demands on the ink. Rotogravure inks are formulated for engraved cylinders and generally require different viscosity and drying characteristics than flexographic inks used on photopolymer plates. A press change — even a change of anilox roll — is a reasonable trigger to re-verify ink suitability rather than assuming the previous product will transfer directly.

Ink handling on the press matters too. Circulation temperature, viscosity control, and the cleanliness of press furniture all contribute to consistent print quality. Where a print defect appears, the ink is not always the cause; a systematic review of the press, the plate, and the substrate should accompany any ink-focused investigation.

## Regulatory considerations

Pharmaceutical primary and secondary packaging is subject to the regulatory framework applicable in the market of use. We do not make regulatory approval claims on this website. Suitability for a given pharmaceutical application must be established by the customer through appropriate documentation and testing under actual production conditions. Where technical documentation is available for a specific product, it will be shared on request with qualified customers.

## Application testing before regular supply

For any new ink recommendation, we suggest a structured trial sequence:

1. **Bench trial** — small-quantity print on the customer's substrate at the customer's plant, using representative press settings.
2. **Short production run** — after the bench trial is satisfactory, a limited production quantity is printed and evaluated for legibility, adhesion, and any post-process effects (heat sealing, forming).
3. **Documentation review** — the technical data available for the product is reviewed against the customer's internal specifications and change-control procedures.
4. **Regular supply** — regular orders are placed only once the trial has been formally reviewed and approved by the customer's quality function.

Skipping this sequence — for example, moving straight from a sample dispatch to a full production order — increases the risk of a batch problem that could have been anticipated in a shorter trial.

## Working with your supplier

The relationship between a pharmaceutical manufacturer and its ink supplier should support the trial process, not compete with it. When you talk to ${CO}, expect to be asked about substrate, press, and application in detail before a product is recommended. A supplier that provides an ink recommendation without asking those questions is offering less than the process requires.

If you are considering a change of blister-printing ink — whether to consolidate suppliers, improve print performance, or evaluate alternatives — get in touch and we will walk through the trial process for your specific application. We will typically ask for foil supplier and grade, press platform, line speed, drying arrangement, and the downstream processes the printed foil will encounter, so that the shortlist we propose is genuinely relevant to your line.
`;

const bodyPorousVsNonPorous = `
"Porous" and "non-porous" is one of the first categorisations a printer applies when reviewing an unfamiliar substrate. It sets the direction for ink chemistry, drying, and long-term adhesion. Getting this categorisation wrong is a common root cause of coding and printing problems on pharmaceutical and food manufacturing lines.

At ${CO} we supply specialist inks for a range of substrates. This article explains the practical significance of porosity, the common porous and non-porous surfaces encountered in pharmaceutical manufacturing, and how the categorisation guides ink selection.

## What porosity means for print

A porous surface has micro-scale openings that allow ink solvent — and, to a lesser extent, ink resin — to be absorbed into the substrate. Absorption contributes to drying and to mechanical anchoring of the printed image. A non-porous surface offers no absorption; the ink must dry entirely through evaporation and adhere through surface interactions such as wetting, surface energy match, and, where used, corona or plasma pre-treatment.

The distinction is not always binary. A coated paper carton can behave as porous on the uncoated inside and non-porous on the outer coated face. A varnished label can shift from porous to non-porous depending on the coverage of the varnish. Treat the printed surface, not the base material, as the reference.

## Common porous substrates in pharma

Substrates typically treated as porous in pharmaceutical secondary packaging include:

- Uncoated paperboard cartons
- Uncoated label stock (cellulose-based)
- Corrugated shipping outers
- Tyvek and similar non-woven sterile-barrier materials
- Some medical-grade release papers

For these substrates, water-based or alcohol-based coding inks are often serviceable, provided the solvent balance is compatible with the substrate's absorbency and the line speed.

## Common non-porous substrates

Non-porous substrates commonly seen include:

- Aluminium blister foils (lacquered or unlacquered)
- PVC / PVDC blister film
- Glass vials
- HDPE and PET bottle surfaces
- Coated / varnished cartons
- Polyester-based labels

Non-porous substrates typically require solvent-based inks with tuned resin systems and adequate flash drying. Adhesion is more sensitive to surface contamination — release oils, mould-release compounds, or handling residues can all cause code failure.

## Choosing the right ink chemistry

The chemistry of a coding ink is chosen against the substrate and the code's expected environment. A code that will pass through a subsequent process (autoclaving, hot-water rinse, high-humidity storage) has to be evaluated against those conditions. A code that is only expected to survive dry warehouse storage has a wider window of acceptable chemistries.

We generally recommend the following logic when short-listing:

- **Porous substrate, dry environment** — water-based coding inks or mild-solvent inks are typically first candidates.
- **Non-porous substrate, dry environment** — solvent-based inks with MEK or acetone base are typically first candidates.
- **Non-porous substrate, wet or heat-exposed environment** — evaluate solvent-based inks with additional resistance additives; expect a longer trial.

The final selection is confirmed by application testing, not by product datasheet alone.

## Testing methodology

A minimum test protocol we suggest with customers is:

1. Print a representative code with the candidate ink under normal line conditions.
2. Allow the code to cure for the documented interval.
3. Perform an abrasion test (rub with a defined material, count strokes to failure).
4. Perform a solvent-rub test appropriate to the intended environment.
5. Age the sample for at least 24 hours before repeating the abrasion and solvent tests.
6. Where relevant, expose to the actual downstream process (autoclave, warm-water bath, high-humidity chamber) and repeat.

Any change of substrate — even from the same supplier — is a valid reason to re-run this sequence. Product suitability remains subject to application testing.

If you are trying to work out whether a substrate should be treated as porous or non-porous, or whether the current ink on your line is the right chemistry for the surface it is being printed on, ${CO} can help you structure the evaluation. A short conversation about substrate, print process, and downstream conditions is usually enough to define a productive first trial.
`;

const bodyChoosingCodingInk = `
Coding on pharmaceutical primary and secondary packaging is a small but consequential part of the production process. A missing, illegible, or unreadable code can prevent product release, disrupt distribution, or trigger downstream investigations. The ink chosen for that code — and the process around it — matters more than the code's visual simplicity suggests.

This article outlines the practical decisions involved in choosing a coding ink for pharmaceutical packaging, based on the work ${CO} does with pharmaceutical manufacturers and their coding equipment suppliers.

## Coding requirements in pharma

Pharmaceutical coding typically has to satisfy several parallel requirements:

- **Legibility** for the intended reading method — human, camera, or laser scanner
- **Adhesion** to the specific packaging substrate under the expected handling conditions
- **Durability** through any downstream process the coded packaging encounters
- **Traceability** — the coding equipment and the ink together produce a stable, verifiable mark

A coding ink chosen for one packaging line is not automatically appropriate for another line, even inside the same facility.

## Solvent-based vs water-based

Coding inks fall broadly into solvent-based and water-based families. The choice is driven by the substrate and the environment.

- **Solvent-based inks** — typically use MEK, ethanol, or acetone as the base solvent. They dry quickly on non-porous substrates and can produce durable marks on aluminium foils, PVC, PET, HDPE, and coated cartons. They require appropriate ventilation and handling on the production line.
- **Water-based inks** — typically used on uncoated paperboard, uncoated labels, and other porous substrates. They are less aggressive on operators and equipment but rely on the substrate's absorbency to dry.

Neither family is intrinsically superior; each is selected for the application.

## Cartridge compatibility

Thermal inkjet (TIJ) coders in particular are cartridge-based systems, and the ink and cartridge are engineered together. Using a non-compatible cartridge in a TIJ head can cause code failures, printhead damage, or inconsistent drop formation.

${CO} supplies solvent-compatible and water-based cartridge references intended for common TIJ platforms. The specific compatibility of a cartridge against a specific coder is confirmed through documentation review and an on-line trial before regular supply.

## Substrate considerations

The substrate is the single most important variable in coding ink selection. Practical questions we ask before recommending an ink:

- What is the exact packaging substrate (grade, supplier, coating)?
- Is the print surface pre-treated or coated?
- What downstream processes will the code pass through?
- What is the line speed and code height?
- What reading equipment (camera or scanner) is verifying the code?
- What is the ambient humidity and temperature at the coding station?

A coding ink recommendation that does not account for these variables is a guess.

## Trial evaluation

For every new coding ink we recommend a structured trial:

1. Short bench trial on the customer's substrate, at the customer's plant, using the customer's coder.
2. Documented abrasion, solvent, and — where applicable — heat / humidity testing of the coded sample.
3. A limited production run before regular supply.
4. Formal internal review and change control at the customer's quality function.

The trial produces the evidence base a pharmaceutical quality system needs. It is not a bureaucratic step; it is the mechanism by which a new coding ink becomes an approved consumable on a specific line.

## Common questions during selection

A few practical points come up repeatedly during coding-ink selection conversations:

- **Do we need a fresh trial when the substrate lot changes?** In most cases, yes. Even lots from the same substrate supplier can shift in ways that affect adhesion and drying.
- **How much sample material should we request?** Enough for at least one full bench trial, plus retained samples. A single-cartridge or single-litre sample is often not enough for a meaningful evaluation.
- **Is a single successful trial sufficient?** Ideally the trial covers at least one shift change and one substrate lot. This surfaces the everyday variability that a one-hour print does not.
- **Who signs off the change?** The customer's quality function, informed by the trial record. The supplier's role is to provide the ink, technical documentation, and support — not to authorise the change internally.

Talk to ${CO} if you are evaluating a new coding ink, changing coder platform, or trying to consolidate coding consumables across multiple lines — we will work through the trial with you.
`;

const bodyTijCoding = `
Thermal inkjet (TIJ) is one of the most common coding technologies on pharmaceutical secondary packaging lines. It offers high resolution, a compact print head, and relatively low maintenance compared with alternatives — provided the ink, cartridge, and line conditions are matched to the application.

This article explains how TIJ coding works, why pharmaceutical manufacturers frequently choose it, and the practical considerations for selecting cartridges and consumables. ${CO} supplies HP-compatible TIJ cartridges and supports customers through the trial and evaluation process.

## How TIJ works

A TIJ printhead is built into a cartridge that contains both the ink reservoir and an array of tiny resistive elements. When electrical current is applied to a resistor, it rapidly heats a thin film of ink, creating a vapour bubble that ejects a droplet through a nozzle. The bubble collapses, drawing more ink from the reservoir, ready for the next drop.

The key characteristics of this arrangement:

- **High resolution** — 600 dpi or higher is standard, allowing crisp text, small character heights, and 2D codes.
- **Low maintenance** — the printhead is replaced with each cartridge, so there is no separate printhead servicing.
- **Compact form factor** — TIJ printheads are small and can be mounted in tight positions on a packaging line.
- **Fast changeover** — cartridge swaps are quick, reducing downtime on line-side ink changes.

TIJ is well suited to text, batch codes, 2D data-matrix codes, and small graphic elements on flat or gently curved substrates.

## Why pharma manufacturers choose TIJ

Pharmaceutical manufacturers typically choose TIJ for coding on:

- Cartons (coated and uncoated)
- Labels (paper and film)
- Certain film wrappers
- Some blister foils, subject to compatibility trials

TIJ suits pharmaceutical operations for several practical reasons. First, code legibility for both human and camera-based verification is generally high. Second, the compact printhead can be integrated into existing packaging lines without significant re-engineering. Third, cartridge-based supply reduces the volume of open ink handled by line operators, which supports cleanliness on the production floor.

TIJ is not universal. Highly absorbent substrates, deeply textured surfaces, and specific non-porous substrates may benefit from continuous inkjet (CIJ) or other technologies instead. The right technology is a function of the substrate and the code requirement.

## Cartridge selection

Two broad families of TIJ cartridge are used in pharmaceutical coding:

- **Water-based (aqueous)** — for uncoated paperboard, uncoated labels, and similarly porous substrates. Lower operator handling considerations; drying relies on substrate absorbency.
- **Solvent-based** — for non-porous substrates such as coated cartons, films, foils, and plastics. Faster drying on non-porous surfaces; requires appropriate ventilation and handling.

Within each family, individual cartridge references are tuned for specific substrate types and coding requirements. A cartridge selection should always be validated against the customer's actual line — a bench print is not a substitute for a short line trial.

## Common line-side issues

Common TIJ issues we investigate with customers include:

- **Missing dots or lines** — often a cartridge purge issue or a partially dried nozzle plate; resolvable through routine wipes and correct cartridge storage.
- **Poor adhesion** — usually a substrate / ink mismatch rather than a cartridge problem; requires a re-evaluation of ink chemistry against the substrate.
- **Inconsistent code density** — check cartridge age, print speed, and firing frequency; some cartridges have a documented in-service life once opened.
- **Bleeding on absorbent substrates** — typically a water-based ink on an over-porous substrate; consider a slower line speed, a different cartridge, or a coated substrate.

Each of these has a systematic route to resolution. We can help work through them with the customer's line data.

## Maintenance considerations

TIJ maintenance is generally simple: cap the cartridge when idle, wipe the nozzle plate periodically with the recommended cloth, and rotate stock so cartridges are used within their in-service window. Operators benefit from a short standard procedure covering these steps, and a documented in-service life for each cartridge family.

If you are evaluating TIJ for a new packaging line, changing cartridge references, or troubleshooting a print quality issue, ${CO} can support the evaluation. We will ask about the substrate, the coder platform, and the line environment before proposing a cartridge reference for trial.
`;

const bodyWhyProductionTrials = `
Changing a printing or coding ink on a pharmaceutical production line is not a simple substitution. Even where the new ink is nominally equivalent to the current one, small differences in solvent balance, viscosity, resin system, or additive package can affect print quality, adhesion, drying, and downstream compatibility. A production trial is how those differences are surfaced before they cause a batch problem.

This article explains what a production trial actually tests, the failure modes it detects, and how a pharmaceutical manufacturer can build a supplier evaluation process that is proportionate and practical.

## What a production trial actually tests

A production trial verifies that a candidate ink performs correctly under the customer's actual conditions. Bench and lab tests confirm chemistry-level properties; a production trial confirms:

- Adhesion on the specific substrate and lot in use that week
- Drying at the actual line speed and ambient humidity
- Legibility as read by the customer's own verification cameras
- Compatibility with all downstream processes the printed pack encounters
- Reliability across a representative production run, not just a single sample

Bench data alone can suggest an ink is suitable. Only a trial confirms it.

## Common failure modes

Failure modes routinely surfaced by production trials include:

- **Substrate drift** — a substrate whose supplier or lot has quietly changed, revealed by the new ink not performing as it did on samples.
- **Line-condition sensitivity** — an ink whose adhesion is fine at 22 °C and 50 % RH but degrades at 30 °C and 65 % RH.
- **Downstream process interactions** — an ink that survives handling but fails after autoclaving, sealing, or a warm-water rinse.
- **Verification system mismatch** — a code that is human-readable but fails automated verification because of low contrast, halo, or edge bleed.
- **Nozzle or plate incompatibility** — for coding inks, a chemistry that runs cleanly for the first hour and then begins to clog.

Each of these can produce a batch problem in regular production. Each is detectable in a short, well-designed trial.

## Recommended trial parameters

A proportionate trial protocol we suggest with customers includes:

1. **Duration** — long enough to run at least one full line changeover, one shift change, and one substrate lot change if possible. A one-hour print is not a trial.
2. **Sampling** — printed samples pulled at defined intervals (start, mid-run, end-of-run) and retained for retest.
3. **Test matrix** — abrasion, solvent rub, and any relevant environmental exposure. Ideally at initial cure and after 24 hours.
4. **Verification** — samples read by the customer's own verification cameras or scanners under the customer's own settings.
5. **Documentation** — a short trial record capturing substrate lot, line conditions, ink lot, cartridge lot, and any deviations. This becomes the evidence base for the change-control decision.

Trials that omit one of these steps often fail to catch the problem they were designed to prevent.

## Documentation

The trial record is a valuable artefact beyond the immediate decision. It supports future audits, provides the reference case for troubleshooting after regular supply begins, and is the foundation for any subsequent variation of the ink or substrate. Even a one-page trial record is more useful than an approval decision with no supporting notes.

${CO} shares trial protocol templates on request. A supplier who resists documenting a trial is a supplier worth reconsidering.

## Building a supplier evaluation process

Pharmaceutical manufacturers benefit from a standing supplier evaluation process, so that trials are consistent regardless of which ink or which supplier is being evaluated. A practical evaluation process includes:

- A short technical questionnaire completed by the supplier
- Documentation review by the customer's quality function
- A defined trial protocol appropriate to the ink and substrate
- A formal change-control decision informed by the trial record
- Post-implementation review after the first three production runs

A supplier who cannot support that process is not necessarily unfit; they may simply lack the internal capability to serve pharmaceutical customers well. ${CO} works within customer evaluation processes and can support the documentation the trial requires.

If you are planning an ink change on a pharmaceutical line and want a trial protocol appropriate to your application, get in touch and we will walk through it with you.
`;

const bodyBlisterPrintingFactors = `
Blister-pack printing is a compact process that concentrates a lot of variables into a small print area. When it works well, it is invisible; when it does not, it can hold up a batch. This article summarises the factors that most often affect print performance on blister-pack aluminium foil, based on the applications ${CO} sees across the pharmaceutical manufacturers we work with.

## The foil–ink interface

The interface between the aluminium foil and the printing ink is where most performance issues begin. Foils vary by:

- **Lacquer composition and coverage** — the print side of most pharma foils is lacquered, and small differences in coverage change wetting behaviour.
- **Surface energy** — a foil batch with lower surface energy than expected can produce dewetting, mottling, or poor adhesion.
- **Micro-roughness** — even within one supplier's grade, roughness can vary by lot; this affects the amount of ink transferred and the appearance of the printed image.
- **Storage history** — foil stored in high-humidity or contaminated environments can carry surface contamination that interferes with print.

The practical takeaway: treat every new foil lot as a new variable, especially when a print performance issue appears.

## Press setup and impression pressure

Blister-pack printing is typically performed on rotogravure or, on some lines, flexographic presses. Setup variables that most commonly affect print quality:

- **Impression pressure** — under-pressure produces broken-up marks; over-pressure produces smearing and reduced legibility.
- **Anilox / cylinder condition** — worn engraving reduces ink transfer; contaminated engraving reduces coverage evenly across the image.
- **Plate or cylinder alignment** — misalignment produces registration issues that can be mistaken for ink problems.
- **Web tension** — over-tension or slippage changes the effective press geometry and can shift ink transfer.

When investigating a print defect, a systematic pass through the press setup is a reasonable first step, even where the ink is the newest variable.

## Drying and cure conditions

Blister-pack inks are typically dried through a combination of solvent evaporation and, in some cases, additional cure. Drying variables to check:

- **Oven / dryer temperature** — a small drift in dryer temperature can move an ink from adequately cured to under-cured, particularly at higher line speeds.
- **Web residence time** — if line speed is increased without adjusting dryer output, the effective drying time falls.
- **Ambient humidity** — high-humidity environments slow solvent evaporation of some ink chemistries.
- **Downstream process temperature** — subsequent forming and sealing steps expose the printed image to additional heat, which can affect apparent cure state.

An ink that appears to work on a bench trial but fails in production is often being run at a shorter effective drying time than the bench trial allowed for.

## Ink handling and storage

Blister-pack printing inks are sensitive to how they are handled between delivery and use. Common handling issues:

- **Solvent evaporation during storage** — partially used containers left open change the ink's viscosity and can shift performance.
- **Contamination from cleaning solvents** — press-side cleaning solvents can carry across into the ink if handling procedures are not tight.
- **Age since manufacture** — many inks have a documented shelf life; using ink close to or beyond that window can produce inconsistent behaviour.
- **Storage temperature** — cold storage or high-temperature exposure can affect solvent balance and pigment dispersion.

A short standard operating procedure at press-side — closed lids, defined shelf-life labelling, controlled decanting — resolves most of these issues.

## When to involve your ink supplier

Escalate to your ink supplier when:

- A defect appears that a systematic press and substrate review does not resolve
- A foil supplier or grade changes and the previous ink no longer performs
- A new pack format, orientation, or downstream process is being introduced
- A verification failure appears that is not attributable to the verification system itself
- The team wants to consolidate multiple ink references onto fewer SKUs

Involving the supplier early — with the substrate lot, press settings, line data, and defect samples — shortens the resolution loop. A supplier that expects to be involved only after the internal investigation is exhausted is not being used effectively.

If your team is investigating a blister-pack print performance issue or planning a change of ink, foil, or line, ${CO} can help work through the variables. Product suitability remains subject to application testing under the customer's actual production conditions.
`;

// ---------- FAQ blocks ----------
const faqBlisterInkSelection = [
  {
    q: "How long should a blister-pack ink trial run before regular supply starts?",
    a: "Long enough to include at least one shift change and one substrate lot change if possible. A single short run is not enough to catch drift.",
  },
  {
    q: `Does ${CO} manufacture the blister-pack inks it supplies?`,
    a: `${CO} supplies specialist blister-pack inks from Dubai. Product suitability for a specific pharmaceutical manufacturing application is subject to application testing under the customer's actual production conditions.`,
  },
  {
    q: "Can you supply technical documentation for a candidate blister-pack ink?",
    a: "Yes. Technical documentation for the specific product is available on request to qualified customers as part of the trial and evaluation process.",
  },
];

const faqChoosingCodingInk = [
  {
    q: "Is a water-based coding ink always suitable for paperboard cartons?",
    a: "Not automatically. Water-based inks are usually a first candidate for uncoated paperboard, but coating, print-face varnish, and downstream processes may push the selection towards a different chemistry. Confirm through trial.",
  },
  {
    q: `Does ${CO} supply cartridges for a specific coder platform?`,
    a: `${CO} supplies HP-compatible TIJ cartridges from Dubai. Compatibility with a specific coder platform is confirmed through documentation review and on-line trial.`,
  },
  {
    q: "How many trial samples should we retain from an ink evaluation?",
    a: "Enough to allow re-testing at initial cure and at 24 hours, and to keep a reference retained sample. A short standing sampling protocol is more useful than an ad-hoc decision each time.",
  },
  {
    q: "Can we change coding ink without a formal change-control process?",
    a: "For pharmaceutical packaging, ink changes typically fall under the customer's change-control procedures. The evaluation should be documented so that the change-control decision has a supporting evidence base.",
  },
];

// ---------- 9 additional article bodies (Content batch) ----------

const bodyPoorInkAdhesion = `
Poor ink adhesion is one of the most common defects investigated on industrial printing and coding lines. It often shows up as smudging under handling, delamination after downstream processing, or a code that lifts under a light rub. The root cause is rarely a single factor — most cases resolve into a combination of substrate condition, ink chemistry, environment, and how the printed material is handled.

${CO} supplies specialist printing and coding inks and works with customers to structure evaluation trials. This article walks through the typical adhesion failure modes we see and how to work through them methodically.

## Substrate surface energy fundamentals

Adhesion begins with wetting. If the ink cannot wet the substrate evenly, it will not form the intimate contact that adhesion depends on. Wetting is driven by the substrate's surface energy relative to the ink's surface tension. Common non-porous substrates such as PVC, PET, coated cartons, and lacquered aluminium foils vary meaningfully in surface energy between suppliers and even between lots.

Where wetting is marginal, corona or plasma pre-treatment is sometimes used to raise surface energy. When a treated substrate is stored too long or is contaminated after treatment, surface energy drops back and adhesion suffers. The practical implication is straightforward: if a printed run works and a repeat run does not, the substrate is the first place to look — not the ink.

## Contamination and residue

Surface contamination is the second most common driver of adhesion problems. Typical sources:

- Release oils or slip additives migrating to the surface of films
- Mould-release residues on injection-moulded parts
- Anti-static or anti-fog coatings that were not intended as a print surface
- Handling residues from operators, gloves, or transport packaging
- Cleaning-solvent residue applied to the line just before printing

A contaminated surface can pass a visual inspection and still fail an adhesion test. When a substrate lot behaves unexpectedly, treat contamination as a candidate cause before commissioning a new ink.

## Ink chemistry mismatch

Ink chemistry has to be matched to the substrate. A solvent-based coding ink formulated for aluminium foil is not automatically suitable for a coated carton. A water-based ink that performs on uncoated paperboard may struggle on a varnished label. When an ink is being asked to work outside its intended substrate family, poor adhesion is the predictable symptom.

Chemistry mismatch is a design decision, not an ink defect. The remedy is to reopen the ink shortlist against the actual substrate rather than to escalate the incumbent ink's coverage or drying settings.

## Environmental conditions during printing

Temperature, humidity, and airflow around the print head all shape adhesion. Solvent inks dry through evaporation; a cold or highly humid environment slows evaporation and can leave a soft mark that rubs off. A hot dryer that over-dries an ink can leave a brittle film that adheres poorly to the substrate underneath. Air movement across a curing zone changes drying uniformity from one edge of a web to the other.

The line environment worth checking includes: ambient temperature, relative humidity, dryer setpoint, actual dryer temperature, web residence time in the dryer, and any changes to line speed made since the last successful run.

## Testing adhesion methodically

A useful adhesion test protocol has four stages: initial cure inspection, tape-pull adhesion, solvent-rub adhesion, and a 24-hour aged retest. Each stage should be run against the customer's own acceptance criteria, not a generic benchmark. Where an intended downstream process — heat sealing, autoclaving, hot-water rinse — is part of the packaging journey, the test protocol should include exposure to that process.

${CO} works with customers on this kind of structured evaluation. Product suitability is subject to application testing under the customer's actual production conditions.

## When to escalate to your ink supplier

Adhesion issues that persist after a substrate and environment review are the point at which the ink supplier adds the most value. Useful context to have ready before that conversation:

- The substrate supplier, grade, and lot reference for the current run and the last known-good run
- Any changes to the line environment since the last known-good run
- The results of any bench-level rub or tape-pull tests already performed
- Sample-retention units from both the current run and the last known-good run

A supplier that receives this context can move directly to a shortlist review. A supplier that receives only a symptom typically spends the first conversation asking for the context anyway. Product suitability for a specific application remains subject to application testing on the customer's own production line.
`;

const faqPoorInkAdhesion = [
  {
    q: "How quickly should we expect a candidate ink to reach full adhesion?",
    a: "Most solvent-based coding inks reach handleable adhesion within seconds and full mechanical adhesion within 24 hours. Water-based inks on porous substrates typically reach handleable adhesion within a few seconds too, but full performance may depend on the substrate's absorbency and ambient humidity. Test at both the initial cure point and after 24 hours before committing to regular supply.",
  },
  {
    q: "Is a failed rub test always an ink problem?",
    a: "No. Substrate condition, contamination, and drying environment cause more adhesion failures than the ink formulation itself. Before requesting a change of ink, verify substrate lot, surface treatment history, and the actual dryer temperature at the print station.",
  },
  {
    q: `Can ${CO} share technical documentation for a candidate ink?`,
    a: `Yes. ${CO} is a specialist supplier and can share technical documentation for shortlisted products with qualified customers under the trial process.`,
  },
];

const bodyInfoBeforeTesting = `
A useful ink trial starts before the first sample is dispatched. The single largest driver of a productive trial is the quality of the information exchanged between the customer and the ink supplier at the start. A trial without that context can pass on the customer's line and still be the wrong shortlist for their application.

${CO} works with pharmaceutical manufacturers and their production engineers to structure this exchange. The list below is what we ask for — and why each item matters.

## Machine and print process details

Every ink is formulated with a specific print process in mind. The first block of questions we ask covers:

- Coder or printer make, model, and print technology (TIJ, CIJ, flexographic, rotogravure, laser, etc.)
- Nominal line speed and range of production speeds
- Print head orientation and stand-off distance
- Any print-head heater or dryer settings

A cartridge intended for a specific thermal-inkjet platform will not necessarily perform in a related but different platform. Knowing the exact machine model prevents a candidate ink being trialled outside its intended application.

## Substrate specifications

The substrate is the second block. It is worth asking the substrate supplier for as much of this as you can:

- Substrate type and full grade / part number
- Coating or lacquer on the print side
- Corona or plasma pre-treatment (if any) and time since treatment
- Storage conditions of the substrate in the plant
- Any known lot-to-lot variability the supplier has previously flagged

Substrate variability is a common cause of "the trial worked but production doesn't." Documenting the substrate lot used for the trial makes it possible to correlate future performance issues to substrate changes.

## Existing ink and known issues

If the customer is already coding on the line, the incumbent ink is a useful reference:

- Product name, cartridge reference, or ink code currently in use
- Any known performance issues (adhesion, drying, cartridge life, cost)
- Whether the customer is looking for a like-for-like replacement or a change of chemistry
- Any prior alternatives that were trialled and rejected — and why

Sharing this context prevents a repeat trial of a chemistry that has already been ruled out.

## Production environment

The environment shapes the trial as much as the ink does:

- Ambient temperature and relative humidity at the coding station
- Any downstream processes the printed pack will encounter (heat seal, autoclave, hot-water rinse, cold storage)
- Verification method — camera, scanner, or manual
- Line lighting and any factors that affect visual quality control

An ink that performs perfectly under bench conditions can be marginal under actual line conditions if the environment was not part of the trial design.

## Sample volume, timeline, and evaluation criteria

Finally, the practical exchange:

- What sample volume is needed for a bench trial? For a short production run?
- What is the timeline for the trial? Is there a change-control window?
- What are the customer's acceptance criteria for the trial (legibility, adhesion, drying, verification pass rate)?
- Who signs off the trial on the customer side, and what documentation do they need?

A trial with vague acceptance criteria produces a vague answer. Aligning on the criteria in advance is the cheapest way to protect the trial's usefulness.

## Working the checklist

None of this is complicated, but the trials that go smoothly are the ones where these questions have been answered in writing before the sample is dispatched. ${CO} supplies candidate inks through our international supply network; product suitability is subject to application testing on the customer's actual line. The checklist above is how we get the most out of that testing.

If you are preparing a trial and want a fillable version of this checklist, get in touch. The exchange in advance is usually the difference between a trial that produces a change-control-ready evidence base and one that produces a data set with too many open questions to act on. A pharmaceutical evaluation is expensive in engineering hours; making the trial productive on the first attempt is what protects that spend.

${CO} works this way with pharmaceutical customers and their engineering teams. Product suitability for a specific application is confirmed through application testing under the customer's actual production conditions.
`;

const bodySolventVsWaterBased = `
Solvent-based and water-based coding inks address the same fundamental job — placing legible, adherent text and codes on packaging — but they do so through different chemistries with different practical constraints. Choosing between them is one of the first decisions in any coding-ink evaluation.

This article walks through how each family works, where each fits, and the handling considerations that come with each. ${CO} supplies both families as a specialist supplier.

## How each chemistry works

**Solvent-based inks** dissolve resins and pigments in an organic solvent — commonly methyl ethyl ketone (MEK), ethanol, acetone, or a blend. When the ink is deposited on the substrate, the solvent evaporates and leaves the resin and pigment behind as an adhered film. Drying is primarily an evaporation event, and it is fast on most non-porous substrates.

**Water-based inks** use water as the primary carrier, with pigment dispersed in a resin system engineered for water-based delivery. On a porous substrate, drying is partly by absorption and partly by evaporation. On a non-porous substrate, drying is essentially evaporation only, and water evaporates slower than most organic solvents at ambient temperature.

Understanding this basic distinction predicts most of the practical differences that follow.

## Substrate compatibility differences

Solvent-based inks are typically the first shortlist for non-porous substrates: aluminium foils, PVC and PVDC films, coated cartons, HDPE and PET containers, and varnished labels. The organic solvents are effective at wetting these surfaces and the dried resin adheres well to them.

Water-based inks are typically the first shortlist for porous substrates: uncoated paperboard, uncoated labels, corrugated outers, and non-woven sterile-barrier materials. The substrate's absorbency does part of the drying work, which allows a water-based chemistry to perform well without an aggressive drying stage.

There is overlap in the middle — coated papers, lightly varnished labels, some films — and the correct family is confirmed by application testing rather than by looking at a datasheet.

## Drying and cure considerations

For a line running at high speed on non-porous substrate, solvent-based inks give the shortest reliable drying window. For a slower-line application on porous substrate, water-based inks can dry adequately without any dryer. On a non-porous substrate at high speed, water-based inks generally require additional line-side drying, which adds cost and complexity.

## Handling, storage, and safety

Solvent-based inks contain volatile organic solvents that require appropriate ventilation, PPE, and storage. Local occupational-health and fire-safety regulations apply. Cartridge storage, decanting practice, and disposal of waste ink are all part of the operating routine.

Water-based inks reduce the ventilation, storage, and disposal burden. Operators handle less volatile material and the line environment carries less solvent load. Water-based systems have their own considerations — extended cartridge life once opened can be a factor in low-utilisation lines, and freezing during transport is worth checking against.

Neither family is intrinsically preferable; each is selected against the application.

## Choosing between them

A short heuristic for the shortlist stage:

- Non-porous substrate, high line speed → solvent-based
- Porous substrate, moderate line speed → water-based
- Non-porous substrate, low line speed → either, decided by handling constraints
- Downstream heat-seal, autoclave, or hot-water rinse → evaluate solvent-based with additional resistance additives

The heuristic gets the shortlist right most of the time. The final selection is confirmed by trial. ${CO} can supply candidates from either family for evaluation. Product suitability is subject to application testing under the customer's actual production conditions.

## Common mistakes when switching between families

A few operational patterns that show up when a customer moves from one family to the other:

- **Cross-contamination during changeover.** Solvent and water-based inks are not chemically compatible. Cartridges, reservoirs, and, in some coder platforms, the ink lines have to be cleaned or dedicated to a single chemistry.
- **Under-estimating the drying change.** A move from solvent-based to water-based on a non-porous substrate usually requires additional line-side drying, or a slower line speed, or a change of substrate. Bench trials can look identical while production performance is very different.
- **Assuming a swap is like-for-like.** Because both families are called "coding ink" and both come in visually similar cartridges, teams sometimes treat the switch as a routine consumable change. In pharmaceutical operations it is a change-controlled event and should follow the same trial and documentation process as any other consumable change.
- **Skipping the retained-sample step.** Retaining a sample from the incumbent ink at the switchover is what makes it possible to investigate a later performance drift. Without it, the investigation is starting from scratch.

None of these are unusual and none are difficult to prevent. They are the practical items that make the difference between a smooth transition and one that turns into a two-week investigation.

${CO} works with customers on both families as a specialist supplier.
`;

const faqSolventVsWaterBased = [
  {
    q: "Can I mix solvent-based and water-based inks in the same cartridge or reservoir?",
    a: "No. The two chemistries are not compatible, and mixing them causes clogging, poor drying, and unreliable code quality. Cartridges and reservoirs should be cleaned and, where documented by the coder manufacturer, dedicated to a single chemistry.",
  },
  {
    q: "Are water-based coding inks always safer for operators?",
    a: "Water-based inks reduce the volatile-solvent load on the line, but they still contain resins, pigments, and additives. Safety data sheets should be reviewed for each product and appropriate PPE and handling procedures applied. The reduced ventilation requirement is a practical benefit rather than a claim of safety by itself.",
  },
  {
    q: "How do I decide between the two families for a mixed-substrate production line?",
    a: "For lines that alternate between porous and non-porous substrates, it is usually more reliable to standardise on the family that suits the larger volume and accept the trade-off on the smaller-volume substrate — or to use different coders for different substrates. Trials should confirm this decision.",
  },
];

const bodySubstrateOverPrice = `
Coding and printing consumables look like an easy line item to compare on unit price. Two cartridges look identical, one is 15 % cheaper, the procurement decision writes itself. In practice, ink is one of the smallest input costs in pharmaceutical packaging — and one of the highest-impact if it fails. Selecting ink against substrate rather than against headline price is the practice that separates lines that run reliably from lines that need constant intervention.

This article outlines why price-first buying tends to underperform, how to run a substrate-driven selection instead, and where a specialist supplier fits in the process. ${CO} supplies pharmaceutical printing, coding and production consumables as a specialist supplier.

## Why price-first buying fails

The problem with pricing consumables in isolation is that the visible cost is small and the invisible costs are large. A cartridge that costs less per unit but fails an adhesion test on a specific substrate lot creates:

- Rework or scrapped batches
- Verification failures and line stops
- Investigation time from production and quality functions
- Customer complaints downstream

None of these costs are visible on the purchase order. They only appear when the failed batch is being investigated, and by then the "savings" from the cheaper cartridge are usually a rounding error.

A price-first shortlist also tends to homogenise consumables across substrates that behave differently — a single cartridge reference used across coated cartons, film labels, and blister foils, for example. That works until a substrate lot drifts and the standardised cartridge is no longer inside its intended application window.

## Substrate-driven selection framework

A substrate-driven selection reverses the sequence. Instead of shortlisting cartridges by price and then trying them against the substrate, the substrate profile drives the shortlist. In practice:

1. Document the substrate profile for each coded packaging component — supplier, grade, lot variability, coating, and downstream process.
2. Group packaging components by substrate family (porous, non-porous, film, foil, glass).
3. Build a preferred shortlist of consumables against each substrate family, sourced from a small number of specialist supply partners.
4. Standardise within each substrate family; accept multiple SKUs across families where the chemistries need to differ.
5. Confirm each shortlist entry through an application trial before it is added to the approved-consumable list.

The output is a small, deliberate consumable set that is matched to the packaging portfolio.

## Total cost of print quality issues

A useful exercise for teams making this shift is to look at the last twelve months of coding-related deviations and calculate the total cost. Typical categories include:

- Investigation hours (production + quality)
- Rework or scrap
- Verification-station stops
- Emergency consumable freight to plug a stock-out
- Consultant or supplier callouts

In our experience with pharmaceutical customers, the annualised cost of these events is often meaningfully higher than the entire ink and cartridge budget. That number is what a substrate-driven approach is protecting against.

## Working with your ink supplier on evaluation

A specialist supplier's role in this process is not to sell more ink; it is to help structure the shortlist and the trials. Practical asks:

- Provide technical documentation for candidate products against the customer's substrate profiles.
- Support bench and short-production trials.
- Advise on retention samples and trial documentation.
- Support the change-control decision with an application review.

${CO} works this way with pharmaceutical customers.

## A practical checklist

Before your next consumables purchase, check that you can answer:

- Which substrate family does this cartridge belong to?
- What is the last-trialled substrate lot for this cartridge?
- What is the trial documentation reference?
- What is the alternative cartridge on the approved-consumable list, and when was it last trialled?

If the answers are missing or informal, the consumables list is being managed on price. If the answers are available and current, the list is being managed on suitability. Product suitability is always subject to application testing under the customer's actual production conditions.
`;

const bodyBatchCodingProblems = `
Batch-coding problems on pharmaceutical packaging lines rarely have a single cause. Most are the result of a small drift in one input — substrate, cartridge, or line environment — combined with an operating routine that did not surface the drift early. This article catalogues the batch-coding issues we most often see and the questions that lead to a root cause.

${CO} supplies coding cartridges and inks and works with pharmaceutical manufacturers on line-side investigations.

## Faded or missing print

Faded, missing, or partial prints usually trace to one of three causes:

- **Cartridge issue** — a partially dried nozzle plate, an air bubble in the reservoir, or an aged cartridge past its documented in-service life
- **Line-speed mismatch** — the coder is being asked to fire faster than the cartridge or ink is designed for at the current settings
- **Substrate absorbency drift** — a lot change on the substrate side has moved the effective ink transfer

The systematic first step is a controlled test print at reduced line speed on a known-good substrate. If the code returns to normal, the cause is in line speed or substrate rather than the cartridge itself.

## Smearing and adhesion issues

Smearing at the coding station usually indicates insufficient drying. Adhesion failure usually indicates a substrate mismatch or contamination. The two symptoms look similar in the first minute after printing, so it is worth distinguishing:

- Smearing at the coder is fresh, wet, and comes off on the operator's finger.
- Adhesion failure appears cleanly cured but lifts off under a rub test after normal cure time.

Smearing is often solved by reducing line speed, opening a dryer, or moving to a faster-drying ink chemistry. Adhesion failure is a shortlist review — see the article on poor ink adhesion for the structured walk-through.

## Cartridge and nozzle problems

Common nozzle-level issues:

- Individual nozzles blocked (visible as vertical bands of missing print)
- Uneven drop volume across the print head (visible as inconsistent density)
- Print-head aged past its useful cartridge life (visible as declining print quality that does not respond to cleaning)

Most cartridge platforms have a documented cartridge lifetime and a recommended cleaning routine. Following these prevents the majority of nozzle-level issues before they become batch-affecting. The operating routine at the coder — capping when idle, wiping the nozzle plate with the recommended cloth, rotating stock — matters at least as much as the cartridge selection.

## Environmental factors

Ambient temperature and humidity affect drying and adhesion. High humidity slows solvent evaporation; high temperature can pre-dry the nozzle plate between prints. Airflow across the coder, whether from a nearby fan or a ventilation grille, can dry the code before it wets the substrate properly.

Environmental drift is often seasonal. Coding lines that run stably in winter can develop marginal print quality in summer as ambient conditions change. A useful diagnostic step is to check the coder's ambient temperature and humidity against the cartridge's documented operating range before investigating the cartridge itself.

## Systematic troubleshooting approach

A structured investigation follows this order:

1. **Reproduce the defect** on a controlled sample so both the operator and the engineer are looking at the same event.
2. **Log the line data** at the moment of failure — line speed, cartridge lot, ink lot, substrate lot, ambient conditions.
3. **Change one variable at a time.** A reduced line speed, a swap to a spare cartridge, or a switch to a retained substrate sample each isolates a different cause.
4. **Escalate to the supplier with the data** rather than with a symptom. A specialist supplier can advise more usefully with substrate and cartridge lot references in hand.
5. **Document the root cause** and the fix so the same investigation is not repeated in three months.

${CO} works with customers using this approach. Product suitability for the customer's specific application remains subject to application testing on the actual production line.
`;

const bodyReducingCodingDowntime = `
Coding downtime rarely appears on a pharmaceutical plant's headline KPIs, but it is one of the most persistent sources of production friction. A packaging line stopped for a nozzle wipe or a cartridge changeover is not producing units, and a stopped line at the coding station propagates upstream in minutes. The good news is that most coding downtime is preventable with a set of small operating routines and a stable consumables supply.

This article outlines what usually causes coding-line downtime and how pharmaceutical manufacturers can reduce it. ${CO} supplies coding consumables and works with production teams on this kind of practical improvement.

## What causes coding line downtime

The recurring causes of coding-related downtime in our experience:

- **Cartridge changeovers** happening more often than the cartridge lifetime warrants — usually because the cartridge was not stored or handled correctly.
- **Nozzle-plate wipe cycles** taking too long — usually because the standard operating procedure has drifted or has never been documented.
- **Consumable stock-outs** at the plant — a cartridge is unavailable when needed and the line waits.
- **Cartridge/ink incompatibility** with a substrate lot that has changed — the line is stopped for a substitution or a substrate swap.
- **Verification failures** at the vision station downstream — the coder was fine, but the code was marginal for the camera settings and the line rejects.

Each has a different fix, but a common thread runs through them: a small daily routine at the coder is the highest-return investment.

## Preventive maintenance basics

The preventive maintenance routine that reduces the greatest number of stop events:

- Cap cartridges when the line is idle for more than the coder manufacturer's documented threshold.
- Wipe the nozzle plate with the recommended cloth at the start and end of each shift.
- Log cartridge in-service hours against the manufacturer's documented cartridge life; replace before the end of life rather than at the first bad print.
- Store cartridges under the documented storage conditions and rotate stock so oldest-in is used first.
- Keep the coder's operating environment (ambient temperature, humidity) within the documented range.

None of these steps take more than a few minutes per shift. Their absence is what compounds into hours of downtime per month.

## Cartridge management

For pharmaceutical customers, the practical items:

- Have a single-page reference of approved cartridge references for each coder in the plant.
- Include the substrate the cartridge was last trialled against and the trial reference number.
- Set a reorder point that reflects the actual consumption rate plus lead time from your supplier, not a legacy value.
- Have a documented substitution — for each cartridge, know the approved secondary cartridge and its trial reference.

This last point matters. A single-source cartridge with no approved backup means that the entire line's ability to code depends on one supplier's on-time delivery. A qualified alternate takes weeks to trial and approve; it is not something you can do during a stock-out.

## Consumables supply reliability

Consumable supply is a supply-chain function, not just a purchasing function. The measures that reduce coding downtime on the supply side:

- Two-source strategy for the primary cartridge family, both qualified and approved.
- Documented lead times per supplier, refreshed quarterly.
- Buffer stock sized to the line's actual monthly consumption plus the longer of the two supplier lead times.
- Regular technical touchpoints with the supplier — not only when a problem exists.

${CO} works within customer supply-chain frameworks and can support qualification of a secondary cartridge alongside a primary. Product suitability is subject to application testing under the customer's actual production conditions.

## Building an escalation process

When a coding issue does escalate, having a documented path saves hours:

1. First-line operator response — cap, wipe, replace cartridge from immediate stock.
2. Second-line engineer response — check line data, substrate lot, ambient conditions; log the event.
3. Supplier escalation — with substrate lot, cartridge lot, and line data in hand, not a symptom alone.
4. Post-event review — root cause captured; PM routine updated if needed.

Every step short of the last one is designed to keep the line running. The last step is designed to keep the same issue from returning next month.
`;

const bodyEvaluateAlternativeConsumables = `
Pharmaceutical manufacturers explore alternative coding consumables for several practical reasons — improving performance on a particular substrate, consolidating suppliers, addressing a supply reliability concern, or benchmarking a long-standing incumbent. In each case, the evaluation must be structured enough to produce evidence a pharmaceutical quality function can act on. This article outlines a working framework.

${CO} supports this kind of evaluation with pharmaceutical customers as a specialist supplier of candidate consumables.

## Reasons manufacturers explore alternatives

Common triggers we see:

- A recurring print-quality issue that the incumbent supplier has not resolved
- An impending change in the primary substrate that may push the incumbent outside its intended application
- A supply-chain event (single-source concentration, long lead times, allocation)
- A budget review that asks whether the incumbent is still the best-fit product
- A strategic decision to qualify a second source for a critical consumable

The trigger shapes the evaluation. A performance-driven evaluation needs a tight test protocol; a supply-driven evaluation needs a broader shortlist and less depth per candidate.

## Structured evaluation framework

A practical framework has five stages:

1. **Requirement definition** — what does the consumable need to do, on which substrates, at what line speed, through which downstream processes?
2. **Shortlist** — three to five candidates that on paper fit the requirement. Broader shortlists dilute the evaluation.
3. **Documentation review** — technical data, safety data, and supplier information reviewed by the customer's quality function.
4. **Application trial** — bench trial, then short production run, on the customer's actual substrate and coder.
5. **Change-control decision** — the trial evidence base is reviewed and a decision to add the alternative to the approved-consumable list is documented.

Each stage produces an artefact that supports the next. A shortlist without a written requirement produces an inconsistent trial; a trial without a documentation review can be reopened during an audit.

## Trial parameters that matter

The trial parameters that most often catch application problems:

- **Duration** — long enough to see at least one shift change and, if practical, one substrate lot change.
- **Sampling** — samples pulled at defined intervals and retained for retest at 24 hours and at any documented ageing point.
- **Test matrix** — abrasion, solvent rub, and any relevant environmental exposure (heat seal, autoclave, warm-water rinse).
- **Verification** — samples read by the customer's own verification cameras or scanners at the customer's usual settings.
- **Line environment** — recorded, not assumed. Ambient temperature, humidity, and line speed are trial inputs.

A trial that omits one of these commonly re-surfaces the problem it was designed to prevent within three months of regular supply.

## Documentation and QA sign-off

For pharmaceutical customers, the trial record is a working document with a downstream audience: the quality function, the auditor, and the next engineer to investigate a print issue. A useful trial record includes:

- Substrate supplier, grade, and lot reference
- Candidate consumable, lot, and cartridge references
- Line settings and environmental data
- Test results at initial cure and at 24 hours
- Verification-camera pass rates during the trial
- Observed deviations and their explanations
- The change-control decision and its rationale

None of this needs to be lengthy. A one-page trial record is more useful than a ten-page one that gets skimmed.

## Working with a specialist supplier

A specialist supplier's role is to make the evaluation efficient. Practical asks:

- Provide technical documentation for the candidate against the customer's substrate profile.
- Supply representative trial quantities, not just a single cartridge.
- Support the trial with technical availability during working hours.
- Provide references or examples where the same product has been used in similar applications, without over-claiming.

${CO} works this way with pharmaceutical customers. Product suitability for the customer's specific application remains subject to application testing under the customer's actual production conditions. When the trial concludes, the customer's approved-consumable list is stronger regardless of whether the alternative is adopted, because the incumbent's evidence base has been refreshed too.
`;

const bodySupplyChain = `
Coding consumables are a small line in a pharmaceutical manufacturer's supply plan and a large operational risk. A missing cartridge stops a coding station; a stopped coding station stops the line. Building a supply chain that treats coding consumables as production-critical — rather than as office consumables — is one of the more durable improvements a pharmaceutical operations team can make.

This article outlines the supply-chain risks specific to coding consumables and the practices that reduce them. ${CO} supplies pharmaceutical printing, coding and production consumables from Dubai, and this article draws on the patterns we see with customer supply-chain functions.

## Supply chain risks specific to coding consumables

Coding consumables have a different risk profile from bulk raw materials. The specifics that matter:

- **Low unit volume, high line criticality** — the actual annual spend can be small enough to sit under the radar of a category review while the operational exposure is high.
- **Substrate-specific approval** — a change in cartridge is a change-controlled event; alternatives cannot be brought in on a same-day basis.
- **Cartridge and machine dependency** — a coder platform ties the customer to a family of compatible cartridges; switching platforms is a capital decision.
- **Shelf-life and storage** — many cartridges have a documented shelf life once opened, and some are sensitive to storage conditions in transit.
- **Global logistics exposure** — a shipment held at a port or by a customs delay affects the plant a few weeks later.

A supply-chain approach that treats coding consumables like general office supply consistently under-invests in these risks.

## Single-source vs multi-source considerations

Pharmaceutical manufacturers rightly consolidate suppliers where they can. For coding consumables the practical position is usually a **primary + qualified alternate** rather than pure single-source.

- The primary supplier receives the majority of volume, benefits from a strong forecast, and provides technical support.
- The qualified alternate has been trialled and is on the approved-consumable list, but does not routinely receive orders.
- The two combined mean that a supply disruption at the primary — allocation, quality hold, freight event — does not stop the line.

A single-source position on a critical coding consumable is a decision that should be visible on a risk register, not a default that has accumulated over time.

## Qualification and technical evaluation

Adding a supplier to a coding consumables supply base is a qualification exercise on two axes: commercial and technical. Commercial qualification is standard procurement work — company profile, financial standing, references, quality documentation. Technical qualification is where coding consumables differ:

- The candidate cartridge or ink is trialled on the customer's substrate, on the customer's coder, at the customer's line speed.
- The trial is documented and reviewed by the customer's quality function.
- The alternative is added to the approved-consumable list against a specific application, not as a generic substitute.

Technical qualification takes weeks, not hours. It is not the sort of exercise you can compress during a stock-out. This is why qualifying the alternate in advance — before it is needed — is the operating pattern that pays off.

## Inventory and lead-time strategy

The buffer stock calculation for coding consumables is straightforward once the inputs are collected:

- Documented lead time from each qualified supplier
- Actual consumption rate, from the last six to twelve months of coder logs
- Any seasonality in consumption (product mix changes, market seasonality)
- A safety factor sized to the risk profile of the specific line

A one-week buffer for a critical coder on a global supplier's normal lead time is often too tight. Two to three months of stable inventory, split across the primary and alternate suppliers, is a more typical operating position. This is not overstock — it is the operating cost of running a production line that codes reliably.

## Long-term supplier partnerships

The supplier relationships that reduce risk over time share a few characteristics:

- Regular technical touchpoints — quarterly, not only during an issue
- Shared visibility of forecast and consumption
- Documented escalation contacts on both sides
- Willingness on the supplier side to support the customer's change-control process

${CO} works this way with pharmaceutical customers. Product suitability for a specific application is subject to application testing on the customer's actual production line, and the supply-chain framework above is the operating context that makes that testing durable.
`;

const bodyDistributors = `
Distributors serving pharmaceutical manufacturing customers are frequently asked whether their portfolio covers specialist printing and coding consumables. For many distributors these are adjacent categories rather than existing strengths, and adding them well takes some deliberate work. This article — written primarily for distribution partners rather than for pharmaceutical customers — outlines what to consider.

${CO} is actively developing a distribution partner network across the Middle East, Africa, and Asia for pharmaceutical printing, coding, and production consumables supplied from Dubai.

## Why distributors expand into specialist printing and coding

The commercial reasons distributors add specialist printing and coding products:

- Pharmaceutical manufacturing customers ask for them alongside general consumables.
- The category has a stable, repeat-order profile with limited seasonality.
- Technical differentiation reduces price-only competition.
- Cross-selling into existing accounts is straightforward when the pharmaceutical relationship already exists.

The category is not a volume category. It is a margin and stickiness category. A pharmaceutical customer that trusts a distributor's technical support on coding consumables is less likely to unbundle the rest of their consumables spend.

## Product category overview

The core categories a distributor serving pharmaceutical customers should consider:

- **Blister-pack printing inks** — rotogravure and flexographic inks for aluminium foil blister packs
- **Specialty inks** — indelible inks, poly inks, PVC reducers, and related products
- **Printing aids** — cleaning aids, screen-cleaning aids, and process consumables
- **Coding cartridges** — solvent-based and water-based cartridges compatible with common coder platforms
- **Production consumables** — tapes, gloves, caps, masks, shoe covers, and disposable garments

A distributor does not need to cover every category from day one. Starting with one or two — often coding cartridges plus one specialty ink family — allows the technical capability to build before the portfolio widens.

## Technical service capability requirements

The single largest differentiator for a distributor in this category is technical service capability. Pharmaceutical customers evaluate distributors on:

- Ability to discuss substrate, coder platform, and application in useful detail
- Willingness to structure a trial rather than push a single SKU
- Access to technical documentation for shortlisted products
- Support during a line-side issue, not only during a sales visit

None of this requires an in-house R&D function. It requires a working relationship with the supplier's technical team and a person on the distributor's side who understands the vocabulary and the trial process.

## Sales approach for pharmaceutical customers

Selling specialist consumables into pharmaceutical accounts differs from selling volume consumables. Practical points:

- Decisions involve production, engineering, quality, and procurement — not procurement alone.
- Change-control means that adopting a new consumable takes weeks, not days.
- A short-term price advantage will not displace an incumbent if the trial evidence base is thin.
- Long-term reliability and technical support outweigh initial price for accounts that have experienced supply or quality issues.

The distributor's role in the account is to make the evaluation efficient — bringing the supplier's technical resource to the customer, supporting the trial, and documenting the outcome — rather than to compete on price alone.

## Partnership evaluation framework

For a distributor evaluating a specialist supplier, useful questions:

- What is the supplier's typical trial process and how does it interact with pharmaceutical change-control?
- What technical documentation is available for the shortlisted products?
- What lead times and freight arrangements apply for the distributor's target markets?
- What territory or exclusivity terms are on offer, and against what performance expectations?
- What is the escalation path for a line-side technical issue?

A supplier that engages meaningfully with these questions is easier to sell for and easier to service. A supplier that offers only price and volume is a different kind of relationship — one that does not usually reward the distributor's technical investment.

${CO} works with distribution partners across the Middle East, Africa, and Asia on this basis. Product suitability for any specific customer application remains subject to application testing under the customer's actual production conditions. Distributors interested in exploring a partnership can start the conversation through the Become a Distributor page.
`;


// ---------- Article registry ----------
// Order: published first (by date desc), stubs after.
export const BLOG_POSTS = [
  // ---------- Published ----------
  {
    slug: "how-to-select-ink-for-pharmaceutical-blister-printing",
    title: "How to Select Ink for Pharmaceutical Blister Printing",
    excerpt: "Ink selection for blister-pack printing depends on foil substrate, print process, drying, and application testing before regular supply.",
    category: "Blister Printing",
    publishedDate: "2026-06-04",
    status: "published",
    seoTitle: `How to Select Ink for Pharmaceutical Blister Printing | ${CO}`,
    seoDescription: "A practical guide to selecting ink for pharmaceutical blister printing — substrate compatibility, press process, regulatory considerations, and trial workflow.",
    heroImageKey: "sample",
    body: bodyBlisterInkSelection,
    faq: faqBlisterInkSelection,
    relatedSlugs: [
      "blister-pack-printing-key-factors-affecting-print-performance",
      "why-production-trials-matter-when-changing-printing-ink",
      "selecting-ink-based-on-substrate-rather-than-price",
    ],
  },
  {
    slug: "understanding-porous-vs-non-porous-printing-applications",
    title: "Understanding Porous vs Non-Porous Printing Applications",
    excerpt: "Porous vs non-porous is one of the first calls a printer makes. Getting it right guides ink chemistry, drying, and long-term adhesion.",
    category: "Substrates",
    publishedDate: "2026-06-18",
    status: "published",
    seoTitle: `Understanding Porous vs Non-Porous Printing Applications | ${CO}`,
    seoDescription: "How porosity affects ink selection — porous vs non-porous substrates in pharmaceutical manufacturing, ink chemistry choices, and a practical test protocol.",
    heroImageKey: "sample",
    body: bodyPorousVsNonPorous,
    relatedSlugs: [
      "selecting-ink-based-on-substrate-rather-than-price",
      "how-to-select-ink-for-pharmaceutical-blister-printing",
      "common-causes-of-poor-ink-adhesion-in-industrial-printing",
    ],
  },
  {
    slug: "choosing-coding-ink-for-pharmaceutical-packaging",
    title: "Choosing Coding Ink for Pharmaceutical Packaging",
    excerpt: "Coding ink choice depends on substrate, environment, cartridge compatibility, and the trial that turns a candidate into an approved consumable.",
    category: "Coding & Marking",
    publishedDate: "2026-07-02",
    status: "published",
    seoTitle: `Choosing Coding Ink for Pharmaceutical Packaging | ${CO}`,
    seoDescription: "Choosing coding ink for pharmaceutical packaging — solvent-based vs water-based, cartridge compatibility, substrate considerations, and trial evaluation.",
    heroImageKey: "coding",
    body: bodyChoosingCodingInk,
    faq: faqChoosingCodingInk,
    relatedSlugs: [
      "tij-coding-in-pharmaceutical-manufacturing",
      "understanding-solvent-based-and-water-based-coding-inks",
      "why-production-trials-matter-when-changing-printing-ink",
    ],
  },
  {
    slug: "tij-coding-in-pharmaceutical-manufacturing",
    title: "TIJ Coding in Pharmaceutical Manufacturing",
    excerpt: "How thermal inkjet coding fits pharmaceutical secondary packaging — cartridge families, line-side issues, and maintenance in practice.",
    category: "Coding & Marking",
    publishedDate: "2026-07-16",
    status: "published",
    seoTitle: `TIJ Coding in Pharmaceutical Manufacturing | ${CO}`,
    seoDescription: "TIJ coding in pharmaceutical packaging — how it works, why manufacturers choose it, cartridge selection, and common line-side issues.",
    heroImageKey: "coding",
    body: bodyTijCoding,
    relatedSlugs: [
      "choosing-coding-ink-for-pharmaceutical-packaging",
      "understanding-solvent-based-and-water-based-coding-inks",
      "reducing-coding-downtime-on-pharmaceutical-production-lines",
    ],
  },
  {
    slug: "why-production-trials-matter-when-changing-printing-ink",
    title: "Why Production Trials Matter When Changing Printing Ink",
    excerpt: "Bench tests suggest an ink is suitable. Only a production trial confirms it under the customer's actual conditions.",
    category: "Application Guidance",
    publishedDate: "2026-07-30",
    status: "published",
    seoTitle: `Why Production Trials Matter When Changing Printing Ink | ${CO}`,
    seoDescription: "Why production trials matter when changing printing ink — failure modes, trial parameters, documentation, and building a supplier evaluation process.",
    heroImageKey: "facility",
    body: bodyWhyProductionTrials,
    relatedSlugs: [
      "how-to-select-ink-for-pharmaceutical-blister-printing",
      "choosing-coding-ink-for-pharmaceutical-packaging",
      "what-information-should-be-provided-before-testing-a-new-industrial-ink",
    ],
  },
  {
    slug: "blister-pack-printing-key-factors-affecting-print-performance",
    title: "Blister-Pack Printing: Key Factors Affecting Print Performance",
    excerpt: "Foil interface, press setup, drying, and ink handling — the everyday variables that decide whether a blister-pack print run holds up.",
    category: "Blister Printing",
    publishedDate: "2026-08-06",
    status: "published",
    seoTitle: `Blister-Pack Printing: Key Factors Affecting Print Performance | ${CO}`,
    seoDescription: "Key factors that affect blister-pack print performance — foil–ink interface, press setup, drying, ink handling, and when to involve your supplier.",
    heroImageKey: "blister",
    body: bodyBlisterPrintingFactors,
    relatedSlugs: [
      "how-to-select-ink-for-pharmaceutical-blister-printing",
      "common-causes-of-poor-ink-adhesion-in-industrial-printing",
      "why-production-trials-matter-when-changing-printing-ink",
    ],
  },

  // ---------- Published (previously stubs, filled in the content batch) ----------
  {
    slug: "common-causes-of-poor-ink-adhesion-in-industrial-printing",
    title: "Common Causes of Poor Ink Adhesion in Industrial Printing",
    excerpt: "A structured walk-through of substrate, surface energy, drying, and handling factors that most often cause adhesion problems.",
    category: "Application Guidance",
    publishedDate: "2025-08-22",
    status: "published",
    seoTitle: `Common Causes of Poor Ink Adhesion in Industrial Printing | ${CO}`,
    seoDescription: "Common root causes of poor ink adhesion in industrial printing — substrate energy, contamination, chemistry mismatch, and how to test adhesion methodically.",
    heroImageKey: "sample",
    body: bodyPoorInkAdhesion,
    faq: faqPoorInkAdhesion,
    relatedSlugs: [
      "understanding-solvent-based-and-water-based-coding-inks",
      "blister-pack-printing-key-factors-affecting-print-performance",
      "why-production-trials-matter-when-changing-printing-ink",
    ],
  },
  {
    slug: "what-information-should-be-provided-before-testing-a-new-industrial-ink",
    title: "What Information Should Be Provided Before Testing a New Industrial Ink?",
    excerpt: "Substrate, coder, line conditions, and downstream processes — the information that turns a candidate ink into a productive trial.",
    category: "Application Guidance",
    publishedDate: "2025-09-19",
    status: "published",
    seoTitle: `What Information Should Be Provided Before Testing a New Industrial Ink? | ${CO}`,
    seoDescription: "A practical checklist of the machine, substrate, environment and evaluation-criteria details a supplier needs before recommending an industrial ink for trial.",
    heroImageKey: "sample",
    body: bodyInfoBeforeTesting,
    relatedSlugs: [
      "common-causes-of-poor-ink-adhesion-in-industrial-printing",
      "why-production-trials-matter-when-changing-printing-ink",
      "how-pharmaceutical-manufacturers-can-evaluate-alternative-coding-consumables",
    ],
  },
  {
    slug: "understanding-solvent-based-and-water-based-coding-inks",
    title: "Understanding Solvent-Based and Water-Based Coding Inks",
    excerpt: "The practical differences between solvent-based and water-based coding inks — chemistry, substrate fit, and handling considerations.",
    category: "Coding & Marking",
    publishedDate: "2025-10-17",
    status: "published",
    seoTitle: `Understanding Solvent-Based and Water-Based Coding Inks | ${CO}`,
    seoDescription: "Solvent-based vs water-based coding inks for pharmaceutical packaging — how each chemistry works, substrate fit, drying, handling and how to choose between them.",
    heroImageKey: "coding",
    body: bodySolventVsWaterBased,
    faq: faqSolventVsWaterBased,
    relatedSlugs: [
      "choosing-coding-ink-for-pharmaceutical-packaging",
      "tij-coding-in-pharmaceutical-manufacturing",
      "understanding-porous-vs-non-porous-printing-applications",
    ],
  },
  {
    slug: "selecting-ink-based-on-substrate-rather-than-price",
    title: "Selecting Ink Based on Substrate Rather Than Price",
    excerpt: "Why the cheapest ink is rarely the cheapest decision once trials, downtime, and rework are counted.",
    category: "Substrates",
    publishedDate: "2025-11-14",
    status: "published",
    seoTitle: `Selecting Ink Based on Substrate Rather Than Price | ${CO}`,
    seoDescription: "Why pharmaceutical manufacturers benefit from selecting ink against substrate rather than headline price — framework, total cost of quality, and a practical checklist.",
    heroImageKey: "sample",
    body: bodySubstrateOverPrice,
    relatedSlugs: [
      "understanding-porous-vs-non-porous-printing-applications",
      "how-to-select-ink-for-pharmaceutical-blister-printing",
      "building-a-reliable-pharmaceutical-coding-consumables-supply-chain",
    ],
  },
  {
    slug: "common-batch-coding-problems-and-their-causes",
    title: "Common Batch-Coding Problems and Their Causes",
    excerpt: "The batch-coding issues that show up most often on packaging lines, and the questions that lead to a root cause.",
    category: "Coding & Marking",
    publishedDate: "2025-12-12",
    status: "published",
    seoTitle: `Common Batch-Coding Problems and Their Causes | ${CO}`,
    seoDescription: "A working reference on common batch-coding problems — faded print, smearing, cartridge issues, environmental factors — and a systematic troubleshooting approach.",
    heroImageKey: "coding",
    body: bodyBatchCodingProblems,
    relatedSlugs: [
      "reducing-coding-downtime-on-pharmaceutical-production-lines",
      "common-causes-of-poor-ink-adhesion-in-industrial-printing",
      "tij-coding-in-pharmaceutical-manufacturing",
    ],
  },
  {
    slug: "reducing-coding-downtime-on-pharmaceutical-production-lines",
    title: "Reducing Coding Downtime on Pharmaceutical Production Lines",
    excerpt: "How consumable choice, cartridge handling, and line-side routines add up to less coding-related downtime.",
    category: "Coding & Marking",
    publishedDate: "2026-01-16",
    status: "published",
    seoTitle: `Reducing Coding Downtime on Pharmaceutical Production Lines | ${CO}`,
    seoDescription: "Practical measures pharmaceutical manufacturers can take to reduce coding-related downtime — preventive maintenance, cartridge management, supply reliability, escalation.",
    heroImageKey: "coding",
    body: bodyReducingCodingDowntime,
    relatedSlugs: [
      "common-batch-coding-problems-and-their-causes",
      "building-a-reliable-pharmaceutical-coding-consumables-supply-chain",
      "how-pharmaceutical-manufacturers-can-evaluate-alternative-coding-consumables",
    ],
  },
  {
    slug: "how-pharmaceutical-manufacturers-can-evaluate-alternative-coding-consumables",
    title: "How Pharmaceutical Manufacturers Can Evaluate Alternative Coding Consumables",
    excerpt: "A structured approach to evaluating an alternative coding consumable — from documentation review through line trial to change control.",
    category: "Coding & Marking",
    publishedDate: "2026-02-20",
    status: "published",
    seoTitle: `How Pharmaceutical Manufacturers Can Evaluate Alternative Coding Consumables | ${CO}`,
    seoDescription: "A structured framework for evaluating an alternative coding consumable — from requirement definition and shortlist through documentation, trial and change-control decision.",
    heroImageKey: "coding",
    body: bodyEvaluateAlternativeConsumables,
    relatedSlugs: [
      "choosing-coding-ink-for-pharmaceutical-packaging",
      "why-production-trials-matter-when-changing-printing-ink",
      "building-a-reliable-pharmaceutical-coding-consumables-supply-chain",
    ],
  },
  {
    slug: "building-a-reliable-pharmaceutical-coding-consumables-supply-chain",
    title: "Building a Reliable Pharmaceutical Coding Consumables Supply Chain",
    excerpt: "How to structure a coding consumables supply chain so that changeovers, stock-outs, and specification drift are the exception.",
    category: "Supply Chain",
    publishedDate: "2026-03-27",
    status: "published",
    seoTitle: `Building a Reliable Pharmaceutical Coding Consumables Supply Chain | ${CO}`,
    seoDescription: "Supply chain practices for pharmaceutical coding consumables — dual sourcing, technical qualification, inventory and lead-time strategy, and long-term supplier partnerships.",
    heroImageKey: "facility",
    body: bodySupplyChain,
    relatedSlugs: [
      "reducing-coding-downtime-on-pharmaceutical-production-lines",
      "how-pharmaceutical-manufacturers-can-evaluate-alternative-coding-consumables",
      "how-distributors-can-add-specialist-pharmaceutical-printing-products-to-their-portfolio",
    ],
  },
  {
    slug: "how-distributors-can-add-specialist-pharmaceutical-printing-products-to-their-portfolio",
    title: "How Distributors Can Add Specialist Pharmaceutical Printing Products to Their Portfolio",
    excerpt: "What distributors should consider when adding specialist pharmaceutical printing and coding products to an existing portfolio.",
    category: "Distribution",
    publishedDate: "2026-05-08",
    status: "published",
    seoTitle: `How Distributors Can Add Specialist Pharmaceutical Printing Products to Their Portfolio | ${CO}`,
    seoDescription: "A practical guide for distributors evaluating specialist pharmaceutical printing and coding consumables — category overview, technical service, sales approach and partnership evaluation.",
    heroImageKey: "facility",
    body: bodyDistributors,
    relatedSlugs: [
      "building-a-reliable-pharmaceutical-coding-consumables-supply-chain",
      "choosing-coding-ink-for-pharmaceutical-packaging",
      "how-pharmaceutical-manufacturers-can-evaluate-alternative-coding-consumables",
    ],
  },
];

// Compute readingTimeMin post-hoc for published articles
BLOG_POSTS.forEach((p) => {
  p.readingTimeMin = p.body ? readingTimeFor(p.body) : null;
});

// ---------- Helpers ----------
export const getBySlug = (slug) => BLOG_POSTS.find((p) => p.slug === slug) || null;

export const getRelated = (post, count = 3) => {
  if (!post) return [];
  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug);
  if (Array.isArray(post.relatedSlugs) && post.relatedSlugs.length) {
    const selected = post.relatedSlugs
      .map((s) => others.find((p) => p.slug === s))
      .filter(Boolean)
      .slice(0, count);
    if (selected.length >= count) return selected;
    // Top up with same-category first, then remaining, avoiding duplicates.
    const usedSlugs = new Set(selected.map((s) => s.slug));
    const sameCat = others.filter((p) => p.category === post.category && !usedSlugs.has(p.slug));
    const rest = others.filter((p) => p.category !== post.category && !usedSlugs.has(p.slug));
    return [...selected, ...sameCat, ...rest].slice(0, count);
  }
  const sameCat = others.filter((p) => p.category === post.category);
  const rest = others.filter((p) => p.category !== post.category);
  return [...sameCat, ...rest].slice(0, count);
};

// Sort helper: published first (date desc), stubs after (alphabetical by title).
export const sortPosts = (list) => {
  const published = list.filter((p) => p.status === "published")
    .sort((a, b) => (a.publishedDate < b.publishedDate ? 1 : -1));
  const stubs = list.filter((p) => p.status === "stub")
    .sort((a, b) => a.title.localeCompare(b.title));
  return [...published, ...stubs];
};
